/**
 * 🟥 CUADRO NEGRO 2026-07-25 P12 · Modal expandido con TODAS las categorías
 * de variantes IA + soporte para modo "recompose" (Generar Imagen Nueva
 * a partir del Busto).
 *
 * Cambios sobre P7.2:
 *   - Grilla scrolleable con 9 categorías: Estilos, Colores, Iluminación,
 *     Texturas, Ambiente, Fondo, Clima, Cámara, Estética fotográfica.
 *   - Chips clickeables que auto-completan el textarea (append + separador).
 *   - Prop `mode`: 'variant' (default) o 'recompose'. Cambia título/intro/
 *     placeholder para el flujo "Generar Imagen Nueva desde el Busto".
 *   - `onExecute({ customInstructions, dontShowAgain, mode })`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Sparkles, X, Play, Palette, Sun, Camera, Cloud, Layers, Focus, Frame, Wand2 } from 'lucide-react';

export const SKIP_VARIANT_EXPLAINER_KEY = 'damyxia_skip_variant_explainer';
export const SKIP_RECOMPOSE_EXPLAINER_KEY = 'damyxia_skip_recompose_explainer';

/* Categorías definidas exactamente como pidió Marcelo en P12 */
const CATEGORIES = {
  es: [
    { id: 'styles', icon: Palette, label: 'Estilos', color: 'text-cyan-300',
      chips: ['artística', 'pintura al óleo', 'acuarela', 'anime', 'cómic', 'fotografía profesional',
              'vintage', 'cyberpunk', 'futurista', 'barroco', 'estilo Van Gogh', 'estilo Picasso',
              'realista', 'hiperrealista'] },
    { id: 'colors', icon: Palette, label: 'Colores', color: 'text-pink-300',
      chips: ['pastel', 'colores vibrantes', 'colores suaves', 'colores cálidos', 'colores fríos',
              'monocromática', 'desaturada', 'saturada'] },
    { id: 'lighting', icon: Sun, label: 'Iluminación', color: 'text-yellow-300',
      chips: ['iluminación brillante', 'iluminación oscura', 'luz de estudio',
              'iluminación cinematográfica', 'luz suave', 'luz dura', 'HDR'] },
    { id: 'textures', icon: Focus, label: 'Texturas', color: 'text-emerald-300',
      chips: ['nítida', 'suave', 'detallada', 'difuminada', 'granulada', 'limpia'] },
    { id: 'mood', icon: Wand2, label: 'Ambiente visual', color: 'text-fuchsia-300',
      chips: ['profesional', 'artístico', 'dramático', 'elegante', 'minimalista', 'vibrante'] },
    { id: 'background', icon: Layers, label: 'Fondo (estilización)', color: 'text-indigo-300',
      chips: ['fondo suave', 'fondo oscuro', 'fondo brillante', 'fondo artístico',
              'fondo difuminado', 'fondo cinematográfico'] },
    { id: 'weather', icon: Cloud, label: 'Clima visual', color: 'text-sky-300',
      chips: ['con lluvia', 'con neblina', 'atardecer', 'escena nocturna'] },
    { id: 'camera', icon: Camera, label: 'Cámara', color: 'text-orange-300',
      chips: ['profundidad de campo', 'enfoque preciso', 'desenfoque suave', 'bokeh', 'gran angular'] },
    { id: 'aesthetic', icon: Frame, label: 'Estética fotográfica', color: 'text-rose-300',
      chips: ['calidad DSLR', 'retrato profesional', 'editorial', 'revista de autos'] },
  ],
  en: [
    { id: 'styles', icon: Palette, label: 'Styles', color: 'text-cyan-300',
      chips: ['artistic', 'oil painting', 'watercolor', 'anime', 'comic', 'professional photography',
              'vintage', 'cyberpunk', 'futuristic', 'baroque', 'Van Gogh style', 'Picasso style',
              'realistic', 'hyperrealistic'] },
    { id: 'colors', icon: Palette, label: 'Colors', color: 'text-pink-300',
      chips: ['pastel', 'vibrant colors', 'soft colors', 'warm colors', 'cool colors',
              'monochromatic', 'desaturated', 'saturated'] },
    { id: 'lighting', icon: Sun, label: 'Lighting', color: 'text-yellow-300',
      chips: ['bright lighting', 'dark lighting', 'studio light', 'cinematic lighting',
              'soft light', 'hard light', 'HDR'] },
    { id: 'textures', icon: Focus, label: 'Textures', color: 'text-emerald-300',
      chips: ['sharp', 'soft', 'detailed', 'blurred', 'grainy', 'clean'] },
    { id: 'mood', icon: Wand2, label: 'Visual mood', color: 'text-fuchsia-300',
      chips: ['professional', 'artistic', 'dramatic', 'elegant', 'minimalist', 'vibrant'] },
    { id: 'background', icon: Layers, label: 'Background (stylization)', color: 'text-indigo-300',
      chips: ['soft background', 'dark background', 'bright background', 'artistic background',
              'blurred background', 'cinematic background'] },
    { id: 'weather', icon: Cloud, label: 'Visual weather', color: 'text-sky-300',
      chips: ['rainy', 'foggy', 'sunset', 'night scene'] },
    { id: 'camera', icon: Camera, label: 'Camera', color: 'text-orange-300',
      chips: ['depth of field', 'sharp focus', 'soft blur', 'bokeh', 'wide angle'] },
    { id: 'aesthetic', icon: Frame, label: 'Photographic aesthetic', color: 'text-rose-300',
      chips: ['DSLR quality', 'professional portrait', 'editorial', 'car magazine'] },
  ],
};

const T = {
  es: {
    variantTitle: 'Crear una Variante de tu Imagen',
    recomposeTitle: 'Generar Imagen Nueva a partir del Busto',
    variantIntro: (
      <>
        La función <strong className="text-cyan-200">Crear Variante</strong> genera una
        nueva versión de tu imagen usando IA (image-to-image). Mantiene la composición
        original y cambia estilo, color, iluminación, texturas o ambiente.
      </>
    ),
    recomposeIntro: (
      <>
        <strong className="text-fuchsia-200">Generar Imagen Nueva a partir del Busto</strong>{' '}
        mantiene el objeto o sujeto principal (auto, persona, producto) y re-genera
        <strong className="text-fuchsia-200"> escena, fondo, ambiente y composición</strong>{' '}
        completamente nuevos. Ideal para probar el mismo producto en otro contexto.
      </>
    ),
    quickTagsLabel: 'Elegí uno o más aspectos (opcional):',
    quickTagsHint: 'Tocá los chips y se van agregando a tu prompt. También podés escribir texto libre.',
    customPromptLabel: 'Prompt personalizado:',
    customPromptPlaceholderVariant: 'Ej: "acuarela + luz cinematográfica + atardecer"…',
    customPromptPlaceholderRecompose: 'Ej: "sobre montaña nevada, atardecer dramático, cinematográfico"…',
    customPromptHint: 'Si lo dejás vacío, se usa un prompt genérico creativo.',
    noteLabel: 'Nota:',
    noteBodyPremium: (
      <>Esta función forma parte del <strong className="text-fuchsia-300">Plan Premium</strong>.</>
    ),
    dontShowAgain: 'No mostrar esta explicación de nuevo',
    executeVariant: 'Ejecutar Variante',
    executeRecompose: 'Generar Imagen Nueva',
    cancel: 'Cancelar',
    closeAria: 'Cerrar',
  },
  en: {
    variantTitle: 'Create a Variant of your Image',
    recomposeTitle: 'Generate a New Image from the Subject',
    variantIntro: (
      <>
        <strong className="text-cyan-200">Create Variant</strong> generates a new version
        of your image using AI (image-to-image). It keeps the original composition and
        changes style, color, lighting, textures or mood.
      </>
    ),
    recomposeIntro: (
      <>
        <strong className="text-fuchsia-200">Generate a New Image from the Subject</strong>{' '}
        keeps the main object or subject (car, person, product) and re-generates a
        completely new <strong className="text-fuchsia-200">scene, background, mood and composition</strong>.
      </>
    ),
    quickTagsLabel: 'Pick one or more aspects (optional):',
    quickTagsHint: 'Tap the chips and they will append to your prompt. You can also type freely.',
    customPromptLabel: 'Custom prompt:',
    customPromptPlaceholderVariant: 'e.g. "watercolor + cinematic light + sunset"…',
    customPromptPlaceholderRecompose: 'e.g. "on a snowy mountain, dramatic sunset, cinematic"…',
    customPromptHint: 'Leave empty to use a generic creative prompt.',
    noteLabel: 'Note:',
    noteBodyPremium: (
      <>This feature is part of the <strong className="text-fuchsia-300">Premium Plan</strong>.</>
    ),
    dontShowAgain: 'Do not show this explainer again',
    executeVariant: 'Run Variant',
    executeRecompose: 'Generate New Image',
    cancel: 'Cancel',
    closeAria: 'Close',
  },
};

export function CreateVariantExplainerModal({
  open,
  onCancel,
  onExecute,
  language = 'es',
  mode = 'variant',
}) {
  const [customInstructions, setCustomInstructions] = useState('');
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    if (open) {
      setCustomInstructions('');
      setDontShowAgain(false);
    }
  }, [open]);

  const t = T[language] || T.es;
  const cats = useMemo(() => CATEGORIES[language] || CATEGORIES.es, [language]);
  const isRecompose = mode === 'recompose';

  if (!open) return null;

  const title = isRecompose ? t.recomposeTitle : t.variantTitle;
  const intro = isRecompose ? t.recomposeIntro : t.variantIntro;
  const placeholder = isRecompose
    ? t.customPromptPlaceholderRecompose
    : t.customPromptPlaceholderVariant;
  const executeLabel = isRecompose ? t.executeRecompose : t.executeVariant;
  const headerIcon = isRecompose ? '🟪' : '🟦';
  const headerAccent = isRecompose ? 'border-fuchsia-500/30' : 'border-cyan-500/30';
  const executeGradient = isRecompose
    ? 'from-fuchsia-500 to-orange-500 hover:from-fuchsia-400 hover:to-orange-400'
    : 'from-cyan-500 to-fuchsia-500 hover:from-cyan-400 hover:to-fuchsia-400';

  const handleChipClick = (chip) => {
    setCustomInstructions((prev) => {
      const clean = (prev || '').trim();
      if (!clean) return chip;
      // Evita duplicar si el chip ya está incluido
      if (clean.toLowerCase().includes(chip.toLowerCase())) return clean;
      return `${clean}, ${chip}`;
    });
  };

  const handleExecute = () => {
    onExecute?.({
      customInstructions: customInstructions.trim(),
      dontShowAgain,
      mode,
    });
  };

  return (
    <div
      className="fixed inset-0 z-[9500] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onCancel}
      data-testid="create-variant-explainer-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-variant-explainer-title"
    >
      <div
        className={`bg-slate-900 border ${headerAccent} rounded-2xl max-w-2xl w-full max-h-[92vh] flex flex-col shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
        data-testid="create-variant-explainer-modal"
        data-mode={mode}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-800 bg-slate-900/95 backdrop-blur rounded-t-2xl">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-lg leading-none">{headerIcon}</span>
            <h2
              id="create-variant-explainer-title"
              className="text-lg font-bold text-white truncate"
              data-testid="create-variant-explainer-title"
            >
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors shrink-0"
            aria-label={t.closeAria}
            data-testid="create-variant-explainer-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body scrolleable */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5" data-testid="create-variant-explainer-body">
          <p className="text-sm text-slate-300 leading-relaxed" data-testid="create-variant-explainer-intro">
            {intro}
          </p>

          {/* Grilla scrolleable de categorías */}
          <div>
            <h3 className="text-xs font-semibold text-slate-300 uppercase tracking-wide mb-1">
              {t.quickTagsLabel}
            </h3>
            <p className="text-[11px] text-slate-500 mb-3">{t.quickTagsHint}</p>
            <div
              className="max-h-[36vh] overflow-y-auto pr-1 space-y-3 rounded-lg border border-slate-800 bg-slate-950/40 p-3"
              data-testid="create-variant-categories-scroll"
            >
              {cats.map((cat) => {
                const Icon = cat.icon;
                return (
                  <div key={cat.id} data-testid={`variant-category-${cat.id}`}>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Icon className={`w-3.5 h-3.5 ${cat.color}`} />
                      <span className="text-[11px] font-semibold text-slate-300 uppercase tracking-wide">
                        {cat.label}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {cat.chips.map((chip) => (
                        <button
                          key={chip}
                          type="button"
                          onClick={() => handleChipClick(chip)}
                          className="px-2.5 py-1 text-[11px] rounded-full bg-slate-800/70 hover:bg-slate-700 text-slate-200 border border-slate-700 hover:border-cyan-400/60 transition-all"
                          data-testid={`variant-chip-${cat.id}-${chip.replace(/\s+/g, '-')}`}
                        >
                          {chip}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Prompt libre */}
          <div>
            <label
              htmlFor="create-variant-custom-prompt"
              className="block text-xs font-semibold text-slate-300 uppercase tracking-wide mb-2"
            >
              {t.customPromptLabel}
            </label>
            <textarea
              id="create-variant-custom-prompt"
              data-testid="create-variant-custom-prompt"
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.target.value)}
              placeholder={placeholder}
              rows={3}
              maxLength={800}
              className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/60 resize-none"
            />
            <p className="text-[11px] text-slate-500 mt-1">{t.customPromptHint}</p>
          </div>

          <div
            className="p-3 rounded-lg bg-fuchsia-500/5 border border-fuchsia-500/20 text-xs text-fuchsia-100/90 leading-relaxed flex items-start gap-2"
            data-testid="create-variant-explainer-note"
          >
            <Sparkles className="w-4 h-4 text-fuchsia-300 shrink-0 mt-0.5" />
            <span>
              <strong className="text-fuchsia-200">{t.noteLabel}</strong> {t.noteBodyPremium}
            </span>
          </div>

          <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="create-variant-dont-show-again"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500/40 focus:ring-offset-0"
            />
            <span>{t.dontShowAgain}</span>
          </label>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 z-10 flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 px-5 py-4 border-t border-slate-800 bg-slate-900/95 backdrop-blur rounded-b-2xl">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 sm:flex-initial sm:px-6 px-4 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-medium border border-slate-700 transition-colors"
            data-testid="create-variant-explainer-cancel-btn"
          >
            {t.cancel}
          </button>
          <button
            type="button"
            onClick={handleExecute}
            className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-gradient-to-r ${executeGradient} text-slate-900 text-sm font-semibold transition-colors`}
            data-testid="create-variant-explainer-execute-btn"
          >
            <Play className="w-4 h-4" />
            {executeLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateVariantExplainerModal;
